import * as STD      from '@emersya/mpa-std-lib';
import * as BasketNS from './';

export interface IApplication {
	controllerGET : (descriptor : {
		readonly route : string;
		readonly parameters : Record<string, string>;
		readonly headers : Record<string, string>;
	}) => Promise<Response>;
	controllerPOST : (descriptor : {
		readonly route : string;
		readonly parameters : Record<string, string>;
		readonly headers : Record<string, string>;
		readonly body : BodyInit | null;
	}) => Promise<Response>;

	get code() : string;

	get configurationFileVersionId() : string | null;

	get currentLanguage() : string;

	get currentCountry() : {
		readonly id : string;
		readonly VAT : number;
	};

	get currentCurrency() : {
		readonly id : string;
		readonly symbol : string;
	};

	get globalPricingContext() : {
		// readonly input : BasketNS.Tax.TType | null;
		// readonly output : BasketNS.Tax.TType | null;
		readonly input : 'exclTax' | 'inclTax' | null;
		readonly output : 'exclTax' | 'inclTax' | null;
		generatePricingGetBinding(boundTo : string) : (host : BasketNS.Product | BasketNS.Part | BasketNS.Option) => string;
	};

};

export interface IBasketDescriptor {
	readonly application : IApplication;
	readonly metaData? : STD.object.JSONComplexRecursive | null;
	readonly products : Omit<BasketNS.Product.IDescriptor, 'basket'>[];
};

export interface IBasketProceedCheckoutOneTimesQuoteDescriptor {
	isQuoteRequest : true;
	isOrderRequest : false;
	builderType : string | null;
	useTwoTimesCreation? : false;
};

export interface IBasketProceedCheckoutTwoTimesQuoteDescriptor {
	isQuoteRequest : true;
	isOrderRequest : false;
	builderType : string | null;
	/**
	 * When unset, basket and product creation are done in one step.
	 * When set, the basket is created then callback is called then basketProduct are inserted.
	 **/
	useTwoTimesCreation : true;
	twoTimesCallback : (createdBasketCode : string) => void | Promise<void>;
};

export type TBasketProceedCheckoutQuoteDescriptor =
	IBasketProceedCheckoutOneTimesQuoteDescriptor
	| IBasketProceedCheckoutTwoTimesQuoteDescriptor;

export interface IBasketProceedCheckoutOrderDescriptor {
	isQuoteRequest : false;
	isOrderRequest : true;
	builderType : string | null;
	referenceType : 'reference' | 'basketCode';
};

export interface IBasketProceedCheckoutCommandRefAsRefDescriptor extends IBasketProceedCheckoutOrderDescriptor {
	referenceType : 'reference';
	reference : string;
};

export interface IBasketProceedCheckoutCommandBasketCodeAsRefDescriptor extends IBasketProceedCheckoutOrderDescriptor {
	referenceType : 'basketCode';
};

export type TBasketProceedCheckoutOrderDescriptor = (
	IBasketProceedCheckoutCommandRefAsRefDescriptor |
	IBasketProceedCheckoutCommandBasketCodeAsRefDescriptor
	);

export interface IBasketProceedCheckoutBasketDescriptor {
	isQuoteRequest : false;
	isOrderRequest : false;
	builderType : string | null;
};

export type TBasketProceedCheckoutDescriptor =
	TBasketProceedCheckoutQuoteDescriptor
	| TBasketProceedCheckoutOrderDescriptor
	| IBasketProceedCheckoutBasketDescriptor;

export interface IBasketProceedCheckoutResponse {
	readonly code : string;
};

export interface IBasketSerialized {
	readonly country : string;
	readonly currency : string;
	readonly locale : string;
	readonly state : string;
	readonly metaData : Record<string, any> | null;
	readonly products : [] | [ BasketNS.Product.ISerialized ];
	readonly totalPrice : BasketNS.Price.ISerialized;
	readonly version : number;
	readonly billingContact : BasketNS.Contact | null;
	readonly deliveryContact : BasketNS.Contact | null;
	readonly application : {
		code : string;
		version : string | null;
	};
};

export class Basket extends STD.events.SimpleTarget<Basket> {
	static currentApplication : IApplication | null = null;

	static get safeApp() {
		if (!this.currentApplication) {
			throw STD.errors.user('');
		}
		return this.currentApplication;
	};

	/**
	 * BasketEventTarget is not a node of a tree nor of a graph so disable target filtering
	 **/
	id = null;
	billingContact : BasketNS.Contact | null = null;
	deliveryContact : BasketNS.Contact | null = null;
	readonly application : IApplication;
	readonly version = 14;
	state = 'unpaid';
	isReadyToComputePrice = false;
	metaData : STD.object.JSONComplexRecursive | null = null;
	products = <BasketNS.Product[]> [];
	currentProduct : BasketNS.Product | null = null;
	totalPrice = BasketNS.Price.ofZero(); // autocomputed value
	isInitialized = false;

	get locale() {
		return this.application.currentLanguage;
	};

	get country() {
		return this.application.currentCountry.id;
	};

	get currency() {
		return this.application.currentCurrency.id;
	};

	async activatePriceComputations() {
		this.isReadyToComputePrice = true;
		await this.computePrice();
	};

	async computePrice() {
		if (!this.isReadyToComputePrice) {
			return;
		}
		const newTotalPrice = BasketNS.Price.ofZero();
		const currentProduct = this.currentProduct;
		for (const product of this.products) {
			if(currentProduct){
				if(currentProduct.id === product.id){
					newTotalPrice.add(product.computePrice());
				}
			}else{
				throw new Error('Missing current product for compute price');
			}
		}
		newTotalPrice.cropTo2Decimal();
		this.totalPrice = newTotalPrice;
		await this.dispatchEvent('price-update', {
			details : {
				price : newTotalPrice
			}
		});
		return (this.totalPrice);
	};

	createProduct(
		productSettings : Omit<BasketNS.Product.IDescriptor, 'basket'>
	)
	{
		const product = new BasketNS.Product({
			basket :     this,
			id :         productSettings.id,
			title :      productSettings.title,
			viewerCode : productSettings.viewerCode,
			quantity :   productSettings.quantity,
			metaData :   productSettings.metaData,
			parts :      productSettings.parts,
			pricing :    productSettings.pricing,
			reference :  productSettings.reference,
			gtin :       productSettings.gtin,
			sku :        productSettings.sku
		});
		this.products.push(product);
		return product;
	};

	async createProductAndInitialize(
		productSettings : Omit<BasketNS.Product.IDescriptor, 'basket'>
	)
	{
		const product = this.createProduct(productSettings);
		await product.initialize();
		return product;
	};

	async removeProduct(id : string) {
		const product = this.getProduct(id);
		if (!product) {
			throw STD.errors.user(`Unknown product id<${ id }>!`, {
				id
			});
		}
		const idx = this.products.indexOf(product);
		this.products.splice(idx, 1);
		await this.dispatchEvent('basket-update', {
			subType : 'productRemoved',
			details : {
				product
			}
		});
	};

	getProduct(id : string) {
		return this.products.find(x => x.id === id);
	};

	getProductOrThrown(id : string) {
		const product = this.getProduct(id);
		if (!product) {
			throw STD.errors.user(`Product<${ id }> not found!`, {
				id
			});
		}
		return product;
	};

	getPart(productId : string, partsPath : string[]) {
		const product = this.getProductOrThrown(productId);
		return (product.getPart(partsPath));
	};

	async useProduct(id : string) {
		const product = this.products.find(x => x.id === id);
		if (!product) {
			throw new Error;
		}
		this.currentProduct = product;
		await this.dispatchEvent('basket-update', {
			subType : 'productUsed',
			details : {
				product
			}
		});
	};

	async toggleOption(
		productId : string,
		partsPath : string[],
		optionId : string,
		keepVolume? : boolean
	)
	{
		const part = this.getPart(productId, partsPath);
		if (part.currentOption) {
			await part.unuseOption();
		} else {
			await part.useOption(optionId, keepVolume);
		}
	};

	async useOption(
		productId : string,
		partsPath : string[],
		optionId : string,
		keepVolume? : boolean
	)
	{
		const product = this.getProductOrThrown(productId);
		await product.getPart(partsPath).useOption(optionId, keepVolume);
	};

	async unuseOption(
		productId : string,
		partsPath : string[]
	)
	{
		const product = this.getProductOrThrown(productId);
		await product.getPart(partsPath).unuseOption();
	};

	async useOrUnuseOption(
		productId : string,
		partsPath : string[],
		optionId : string,
		use : boolean,
		keepVolume? : boolean
	)
	{
		if (use) {
			await this.useOption(productId, partsPath, optionId, keepVolume);
		} else {
			const part = this.getPart(productId, partsPath);
			if (part.currentOption && (part.currentOption.id === optionId)) {
				await part.unuseOption();
			}
		}
	};

	async usePartReference(
		productId : string,
		partsPath : string[],
		reference : string | BasketNS.Reference
	)
	{
		if (typeof reference !== 'object') {
			reference = new BasketNS.Reference(reference);
		}
		const product = this.getProductOrThrown(productId);
		const part = product.getPart(partsPath);
		part.reference = reference;
		await this.dispatchEvent('basket-update', {
			subType : 'partReferenceUpdated',
			details : {
				product,
				part,
				reference
			}
		});
	};

	getCustom(
		productId : string,
		partsPath : string[],
		type : BasketNS.TOptionCustomizationTypes,
		id : string
	)
	{
		const product = this.getProductOrThrown(productId);
		const part = product.getPart(partsPath);
		if (!part.currentOption) {
			throw STD.errors.user(
				"Cannot get custom on part which doesn't have a current option!",
				{
					productId,
					partsPath,
					type,
					id
				}
			);
		}
		if (type === 'image') {
			return (part.currentOption.getCustom(type, id));
		} else if (type === 'color') {
			return (part.currentOption.getCustom(type, id));
		} else if (type === 'text') {
			return (part.currentOption.getCustom(type, id));
		} else if (type === 'pattern') {
			return (part.currentOption.getCustom(type, id));
		} else {
			throw STD.errors.library('');
		}
	};

	hasCustom(
		productId : string,
		partsPath : string[],
		type : BasketNS.TOptionCustomizationTypes,
		id : string
	)
	{
		const product = this.getProductOrThrown(productId);
		const part = product.getPart(partsPath);
		if (!part.currentOption) {
			throw STD.errors.user(
				"Cannot get custom on part which doesn't have a current option!",
				{
					productId,
					partsPath,
					type,
					id
				}
			);
		}
		if (type === 'image') {
			return (part.currentOption.hasCustom(type, id));
		} else if (type === 'color') {
			return (part.currentOption.hasCustom(type, id));
		} else if (type === 'text') {
			return (part.currentOption.hasCustom(type, id));
		} else if (type === 'pattern') {
			return (part.currentOption.hasCustom(type, id));
		} else {
			throw STD.errors.library('');
		}
	};

	async addCustom(
		productId : string,
		partsPath : string[],
		customization : BasketNS.TOptionCustomization
	)
	{
		const product = this.getProductOrThrown(productId);
		const part = product.getPart(partsPath);
		if (!part.currentOption) {
			throw STD.errors.user(
				"Cannot get custom on part which doesn't have a current option!",
				{
					productId,
					partsPath,
					customization
				}
			);
		}
		return (await part.currentOption.addCustom(customization));
	};

	async addOrUpdateCustom(
		productId : string,
		partsPath : string[],
		customization : BasketNS.TOptionCustomization
	)
	{
		const product = this.getProductOrThrown(productId);
		const part = product.getPart(partsPath);
		if (!part.currentOption) {
			throw STD.errors.user(
				"Cannot get custom on part which doesn't have a current option!",
				{
					productId,
					partsPath,
					customization
				}
			);
		}
		if (part.currentOption.hasCustom(customization.type, customization.id)) {
			return (await part.currentOption.updateCustom(customization));
		}
		return (await part.currentOption.addCustom(customization));
	};

	async removeCustom(
		productId : string,
		partsPath : string[],
		type : BasketNS.TOptionCustomizationTypes,
		id : string
	)
	{
		const product = this.getProductOrThrown(productId);
		const part = product.getPart(partsPath);
		if (!part.currentOption) {
			throw STD.errors.user(
				"Cannot get custom on part which doesn't have a current option!",
				{
					productId,
					partsPath,
					type
				}
			);
		}
		await part.currentOption.removeCustom(type, id);
	};

	async removeCustomIfUsed(
		productId : string,
		partsPath : string[],
		type : BasketNS.TOptionCustomizationTypes,
		id : string,
		throwIfNoOption : boolean = true
	)
	{
		const product = this.getProductOrThrown(productId);
		const part = product.getPart(partsPath);
		if (!part.currentOption) {
			if (throwIfNoOption) {
				throw STD.errors.user(
					"Cannot get custom on part which doesn't have a current option!",
					{
						productId,
						partsPath,
						type
					}
				);
			}
			return;
		}
		if (part.currentOption.hasCustom(type, id)) {
			await part.currentOption.removeCustom(type, id);
		}
	};

	async proceedCheckout(desc : TBasketProceedCheckoutDescriptor) : Promise<IBasketProceedCheckoutResponse> {
		if (desc.isQuoteRequest && desc.isOrderRequest) {
			throw STD.errors.user('', desc);
		}
		const isUsingTwoTimesCreation = !desc.isQuoteRequest ? false : !!desc.useTwoTimesCreation;
		const entity = desc.isQuoteRequest ? 'quote' : desc.isOrderRequest ? 'order' : 'basket';
		// /app/{appCode}/quote/insertProducts

		let snapshot : IBasketProceedCheckoutResponse = { code : <string> <any> null };

		const proceed = async (isTwoTimes : boolean) => {
			const action = isTwoTimes ? 'insertProducts' : 'create';
			const response = await this.application.controllerPOST({
					route :      `${ entity }/${ action }`,
					parameters : {},
					body :       STD.DOM.formDataFromObject({
						...desc,
						basket : {
							...this.serialize(isUsingTwoTimesCreation && !isTwoTimes),
							...(isTwoTimes ? { code : <string> (<any> snapshot).code } : {})
						}
					}),
					headers :    {
						'Accept' : 'application/json'
					}
				}
			);
			const result = await response.json();
			if (result.resultSet.error) {
				throw STD.errors.library('Error from the server', { error : result.resultSet.error });
			}
			return <IBasketProceedCheckoutResponse> result.resultSet.data[ 0 ];
		};
		snapshot = await proceed(false);
		if (isUsingTwoTimesCreation) {
			await (<IBasketProceedCheckoutTwoTimesQuoteDescriptor> desc).twoTimesCallback(snapshot.code);
			await proceed(true);
		}
		return snapshot;
	};

	async initialize() {
		for (const product of this.products) {
			await product.initialize();
		}
		this.isInitialized = true;
	};

	serialize(ignoreProduct : boolean = false) : IBasketSerialized {
		return {
			country :         this.country,
			currency :        this.currency,
			locale :          this.locale,
			state :           this.state,
			metaData :        this.metaData ? Object.assign({}, this.metaData) : null,
			products :        !ignoreProduct ? (this.currentProduct ? [ this.currentProduct.serialize() ] : []) : [],
			totalPrice :      this.totalPrice.serialize(),
			version :         this.version,
			billingContact :  this.billingContact,
			deliveryContact : this.deliveryContact,
			application :     {
				code :    this.application.code,
				version : this.application.configurationFileVersionId
			}
		};
	};

	private async onBasketUpdated() {
		await this.computePrice();
	};

	constructor(settings : IBasketDescriptor) {
		super({});
		// this.application      = settings.application;
		// if (this.application.basket) {
		// 	throw STD.errors.user('One basket is already initialized!', {
		// 		basket : this.application.basket
		// 	});
		// }
		// this.application.basket = this;
		this.application = settings.application;
		if (settings.metaData) {
			this.metaData = settings.metaData;
		}
		this.addEventListener(
			'basket-update',
			STD.misc.createDebouncer({
				delay : 150,
				fn :    this.onBasketUpdated.bind(this)
			}), {
				listener : this,
				subType :  '*'
			}
		);
		for (const productSettings of settings.products) {
			this.createProduct(productSettings);
		}
	};
};
STD.events.Target.initializeEventsTarget.call(Basket);
STD.events.Target.declareEventType.call(Basket, 'price-update');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'productUsed');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'productRemoved');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'partReferenceUpdated');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'part-customOption-add');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'part-customOption-remove');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'part-customOption-updated');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'option-volume-updated');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'partRemoved');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'partOptionUsed');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'partOptionUnused');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'product-thumbnailsURLs');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'product-configurationCode');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'product-configurationReplayURL');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'product-configuratorReplayURL');
STD.events.Target.declareEventType.call(Basket, 'basket-update', 'product-PDFURL');
